import { useState } from 'react';
import { Badge, Callout, Card, Field, SectionHeading, Select, TextInput } from '@/components/ui';
import { appendAudit } from '@/db/audit';
import { describePeriod, endOfMonth, endOfYear, startOfMonth, startOfYear, todayIso } from '@/domain/dates';
import { formatEur, formatKm } from '@/domain/money';
import { buildExportDataset, type ExportDataset } from '@/export/dataset';
import { downloadBlob } from '@/export/zip';
import { useSession } from '@/session/SessionContext';
import { useT } from '@/i18n';
import { buildVerfahrensdokumentation } from './verfahrensdokumentation';

type Preset = 'monat' | 'jahr' | 'frei';

export function ExportScreen() {
  const session = useSession();
  const t = useT();
  const today = todayIso();
  const currentYear = Number(today.slice(0, 4));

  const [preset, setPreset] = useState<Preset>('monat');
  const [from, setFrom] = useState(startOfMonth(today));
  const [to, setTo] = useState(endOfMonth(today));
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ExportDataset | null>(null);

  function applyPreset(next: Preset, value?: string) {
    setPreset(next);
    setPreview(null);
    if (next === 'monat') {
      const bucket = value ?? today.slice(0, 7);
      setFrom(`${bucket}-01`);
      setTo(endOfMonth(`${bucket}-01`));
    } else if (next === 'jahr') {
      const year = Number(value ?? currentYear);
      setFrom(startOfYear(year));
      setTo(endOfYear(year));
    }
  }

  async function loadDataset(): Promise<ExportDataset> {
    return buildExportDataset({
      dataKey: session.dataKey,
      from,
      to,
      company: session.company,
      app: session.app,
      driver: session.account,
      passengers: session.passengers,
      addresses: session.addresses,
      vehicles: session.vehicles,
    });
  }

  async function run(kind: 'pdf' | 'xlsx' | 'gobd', label: string) {
    setBusy(kind);
    setError(null);
    try {
      const dataset = await loadDataset();
      setPreview(dataset);

      // The three renderers pull in the heaviest dependencies in the app, and
      // most sessions never export. Loading them here keeps the trip-entry
      // screen — the one used every day, often on mobile data — small.
      const slug = `${from}_bis_${to}`;
      if (kind === 'pdf') {
        const { renderLogbookPdf } = await import('@/export/pdf');
        downloadBlob(await renderLogbookPdf(dataset), `Fahrtenbuch_${slug}.pdf`);
      } else if (kind === 'xlsx') {
        const { renderWorkbook } = await import('@/export/xlsx');
        downloadBlob(await renderWorkbook(dataset), `Fahrtenbuch_${slug}.xlsx`);
      } else {
        const { renderGobdArchive } = await import('@/export/gobd');
        downloadBlob(renderGobdArchive(dataset), `GoBD_Archiv_${slug}.zip`);
      }

      // The export itself is a documented event: an auditor asking what was
      // handed over and when should find the answer in the same chain.
      await appendAudit(session.audit, {
        action: 'export',
        entityType: 'system',
        entityId: `export-${kind}-${slug}`,
        entityLabel: `${label} für ${describePeriod(from, to)}`,
        changes: [],
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.exports.exportFailed);
    } finally {
      setBusy(null);
    }
  }

  async function loadPreview() {
    setBusy('preview');
    setError(null);
    try {
      setPreview(await loadDataset());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.exports.previewFailed);
    } finally {
      setBusy(null);
    }
  }

  function downloadVerfahrensdokumentation() {
    const content = buildVerfahrensdokumentation(session.company, session.app, session.account);
    downloadBlob(
      new Blob([content], { type: 'text/markdown;charset=utf-8' }),
      'Verfahrensdokumentation_Fahrtenbuch.md',
    );
  }

  const years = Array.from({ length: 6 }, (_, index) => currentYear - index);
  const months = Array.from({ length: 24 }, (_, index) => {
    const date = new Date(`${today.slice(0, 7)}-01T00:00:00Z`);
    date.setUTCMonth(date.getUTCMonth() - index);
    return date.toISOString().slice(0, 7);
  });

  return (
    <div className="space-y-5">
      <SectionHeading title={t.exports.title} description={t.exports.description} />

      <Card title={t.exports.period}>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t.exports.selection}>
            <Select
              value={preset}
              onChange={(event) => applyPreset(event.target.value as Preset)}
            >
              <option value="monat">{t.exports.month}</option>
              <option value="jahr">{t.exports.year}</option>
              <option value="frei">{t.exports.custom}</option>
            </Select>
          </Field>

          {preset === 'monat' && (
            <Field label={t.exports.month}>
              <Select value={from.slice(0, 7)} onChange={(event) => applyPreset('monat', event.target.value)}>
                {months.map((bucket) => (
                  <option key={bucket} value={bucket}>
                    {bucket}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {preset === 'jahr' && (
            <Field label={t.exports.year}>
              <Select
                value={from.slice(0, 4)}
                onChange={(event) => applyPreset('jahr', event.target.value)}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {preset === 'frei' && (
            <>
              <Field label={t.common.from}>
                <TextInput
                  type="date"
                  value={from}
                  onChange={(event) => {
                    setFrom(event.target.value);
                    setPreview(null);
                  }}
                />
              </Field>
              <Field label={t.common.to}>
                <TextInput
                  type="date"
                  value={to}
                  onChange={(event) => {
                    setTo(event.target.value);
                    setPreview(null);
                  }}
                />
              </Field>
            </>
          )}
        </div>

        <button
          type="button"
          className="btn-secondary mt-3"
          onClick={() => void loadPreview()}
          disabled={busy !== null}
        >
          {busy === 'preview' ? t.exports.checking : t.exports.check}
        </button>
      </Card>

      {preview && (
        <Card title={t.exports.previewTitle(preview.periodLabel)}>
          <div className="grid gap-3 text-sm sm:grid-cols-4">
            <Preview label={t.exports.trips} value={String(preview.totals.tripCount)} />
            <Preview
              label={t.exports.kilometers}
              value={`${formatKm(preview.totals.distanceMeters)} km`}
            />
            <Preview label={t.exports.gross} value={formatEur(preview.totals.grossCents)} />
            <Preview label={t.exports.vat} value={formatEur(preview.totals.vatCents)} />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {preview.chain.ok ? (
              <Badge tone="success">{t.exports.chainOk(preview.chain.entryCount)}</Badge>
            ) : (
              <Badge tone="danger">{t.exports.chainBad(preview.chain.problems.length)}</Badge>
            )}
            {preview.reconciliations.some((row) => row.withinTolerance === false) && (
              <Badge tone="warning">{t.exports.odometerDeviation}</Badge>
            )}
            {preview.shifts.some((shift) => shift.odometerStartMeters === 0) && (
              <Badge tone="warning">{t.exports.odometerMissing}</Badge>
            )}
          </div>

          {!preview.chain.ok && (
            <div className="mt-3">
              <Callout tone="danger" title={t.exports.chainBrokenTitle}>
                {t.exports.chainBrokenBody}
              </Callout>
            </div>
          )}
        </Card>
      )}

      {error && <Callout tone="danger">{error}</Callout>}

      <div className="grid gap-3 md:grid-cols-3">
        <ExportCard
          title="PDF"
          description={t.exports.pdfDescription}
          note={t.exports.pdfNote}
          action={t.exports.pdfAction}
          busyLabel={t.exports.creating}
          busy={busy === 'pdf'}
          disabled={busy !== null}
          onClick={() => void run('pdf', 'PDF-Export')}
        />
        <ExportCard
          title={t.exports.xlsxTitle}
          description={t.exports.xlsxDescription}
          note={t.exports.xlsxNote}
          action={t.exports.xlsxAction}
          busyLabel={t.exports.creating}
          busy={busy === 'xlsx'}
          disabled={busy !== null}
          onClick={() => void run('xlsx', 'Excel-Export')}
        />
        <ExportCard
          title={t.exports.gobdTitle}
          description={t.exports.gobdDescription}
          note={t.exports.gobdNote}
          action={t.exports.gobdAction}
          busyLabel={t.exports.creating}
          busy={busy === 'gobd'}
          disabled={busy !== null}
          highlight
          onClick={() => void run('gobd', 'GoBD-Archiv')}
        />
      </div>

      <Callout tone="info" title={t.exports.whyThreeTitle}>
        {t.exports.whyThreeBody}
      </Callout>

      <Card title={t.exports.verfahrenTitle}>
        <p className="text-muted text-sm leading-relaxed">{t.exports.verfahrenBody}</p>
        <button type="button" className="btn-secondary mt-3" onClick={downloadVerfahrensdokumentation}>
          {t.exports.verfahrenDownload}
        </button>
      </Card>
    </div>
  );
}

function Preview({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-muted text-xs">{label}</p>
      <p className="text-heading mt-0.5 text-lg font-semibold">{value}</p>
    </div>
  );
}

function ExportCard({
  title,
  description,
  note,
  action,
  busyLabel,
  busy,
  disabled,
  highlight,
  onClick,
}: {
  title: string;
  description: string;
  note: string;
  action: string;
  busyLabel: string;
  busy: boolean;
  disabled: boolean;
  highlight?: boolean;
  onClick: () => void;
}) {
  return (
    <div className={`card flex flex-col ${highlight ? 'border-accent/40' : ''}`}>
      <h3 className="text-heading text-sm font-semibold">{title}</h3>
      <p className="text-muted mt-1.5 flex-1 text-sm leading-relaxed">{description}</p>
      <p className="text-muted mt-2 text-xs">{note}</p>
      <button
        type="button"
        className={`mt-3 ${highlight ? 'btn-primary' : 'btn-secondary'}`}
        onClick={onClick}
        disabled={disabled}
      >
        {busy ? busyLabel : action}
      </button>
    </div>
  );
}
